import moment from "moment";
import { splitCsv, routeMatchesCsv } from "./busChangeRouteFilter";

// Route.color is the transportation department color and is not shown on tiles.
// Tiles use route.chiderColor (a BusRouteColors name) and route.roomNumber.

// A route uses a geo stop unless the office set GoeNotRequired.
// An empty geo schedule is not the same thing: the office still has to map it.
export function routeNeedsGeoStop(route) {
  if (!route) return false;
  const flag =
    route.goeNotRequired != null ? route.goeNotRequired : route.GoeNotRequired;
  return flag !== true;
}

export function buildColorMap(colors) {
  const map = {};
  (colors || []).forEach((c) => {
    const name = String((c && c.name) || "").trim().toUpperCase();
    const hex = String((c && c.hex) || "").trim();
    if (name && hex) map[name] = hex;
  });
  return map;
}

export function getRouteBusNumber(route) {
  if (!route) return "";
  const actual = String(route.actualBusNumber || "").trim();
  if (actual) return actual;
  return String(route.busNumber || "").trim();
}

function routeBuildingCount(route) {
  return String((route && (route.buildings || route.Buildings)) || "")
    .split(",")
    .map((b) => b.trim())
    .filter(Boolean).length;
}

// A building's own room and color win.
// A route with one building can still use the route-level values.
// A route with more than one building stays blank for a building that has no row.
export function applyBuildingIdentity(route, building) {
  if (!route) return route;
  const key = String(building || "")
    .trim()
    .toUpperCase();
  if (!key) return route;
  const list = route.buildingIdentities || route.BuildingIdentities || [];
  const match = list.find(
    (item) =>
      String((item && (item.building || item.Building)) || "")
        .trim()
        .toUpperCase() === key
  );
  if (!match) {
    if (routeBuildingCount(route) > 1) {
      return { ...route, roomNumber: "", chiderColor: "" };
    }
    return route;
  }
  const room =
    match.roomNumber != null ? match.roomNumber : match.RoomNumber;
  const color =
    match.chiderColor != null ? match.chiderColor : match.ChiderColor;
  return {
    ...route,
    roomNumber: room || "",
    chiderColor: color || "",
  };
}

export function getChiderColorHex(route, colorMap) {
  const name = String((route && route.chiderColor) || "").trim().toUpperCase();
  if (!name) return null;
  return (colorMap && colorMap[name]) || null;
}

function brightness(hex) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return 255;
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
}

// Room number is the main label when present; bus number is always shown.
// Known chider color paints the tile; otherwise the tile is black and white.
export function getRouteTile(route, colorMap) {
  const busNumber = getRouteBusNumber(route) || "—";
  const room = String((route && route.roomNumber) || "").trim();
  const hex = getChiderColorHex(route, colorMap);
  const colorName = hex ? String(route.chiderColor).trim() : "";

  const main = room ? "Room " + room : "#" + busNumber;
  const subParts = [];
  if (room) subParts.push("#" + busNumber);
  if (colorName) subParts.push(colorName);
  const time = String((route && route.timeStart) || "").trim();
  const weekday = getSingleWeekDay(route);

  let style;
  if (hex) {
    const light = brightness(hex) > 160;
    style = {
      backgroundColor: hex,
      color: light ? "#1b1b1b" : "#ffffff",
      borderColor: brightness(hex) > 200 ? "#bdbdbd" : hex,
    };
  } else {
    style = {
      backgroundColor: "#ffffff",
      color: "#111111",
      borderColor: "#111111",
    };
  }

  return {
    main,
    sub: subParts.join(" · "),
    time,
    weekday,
    room,
    busNumber,
    colorName,
    hex,
    mono: !hex,
    style,
  };
}

const DAY_ORDER = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// One tile per bus + room + chider color, sorted by room then bus number.
export function buildRouteChoices(routes, selectedId, colorMap) {
  const seen = {};
  const items = [];
  (routes || []).forEach((route) => {
    const tile = getRouteTile(route, colorMap);
    const key = [
      tile.busNumber.toUpperCase(),
      tile.room.toUpperCase(),
      tile.colorName.toUpperCase(),
      tile.weekday.toUpperCase(),
    ].join("|");
    if (seen[key]) return;
    seen[key] = true;
    items.push({
      key: key + "|" + route.id,
      route,
      tile,
      selected: selectedId != null && route.id == selectedId,
    });
  });
  const cmp = (a, b) =>
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  const dayRank = (w) => {
    if (!w) return -1;
    const i = DAY_ORDER.indexOf(w.toLowerCase());
    return i === -1 ? 99 : i;
  };
  items.sort((a, b) => {
    // Single-day routes (e.g. Friday) go together at the bottom of the list.
    const da = dayRank(a.tile.weekday) === -1 ? 0 : 1;
    const db = dayRank(b.tile.weekday) === -1 ? 0 : 1;
    if (da !== db) return da - db;
    if (!!a.tile.room !== !!b.tile.room) return a.tile.room ? -1 : 1;
    if (a.tile.room && b.tile.room && a.tile.room !== b.tile.room) {
      return cmp(a.tile.room, b.tile.room);
    }
    return cmp(a.tile.busNumber, b.tile.busNumber);
  });
  return items;
}

// BusChangeRoutes user attribute values, e.g. ["75"].
export function parseBusChangeRouteBuildings(attributes) {
  const values = [];
  (attributes || [])
    .filter((a) => a && String(a.name || "").trim() === "BusChangeRoutes")
    .forEach((a) => {
      splitCsv(a.value).forEach((v) => {
        if (!values.some((x) => x.toUpperCase() === v.toUpperCase())) {
          values.push(v);
        }
      });
    });
  return values;
}

// Day name when the route runs on exactly one weekday; blank otherwise.
export function getSingleWeekDay(route) {
  const days = splitCsv(route && route.weekDays);
  return days.length === 1 ? days[0] : "";
}

// Main picker list: a route that runs on a single weekday (e.g. Friday) only
// belongs there for a one-day bus change that falls on that day. Otherwise it
// is left to the additional routes. The currently selected route always stays.
export function filterSingleDayRoutes(routes, startDate, endDate, keepRoute) {
  const start = startDate ? moment(startDate) : null;
  const end = endDate ? moment(endDate) : null;
  const oneDay =
    !!start && start.isValid() && (!end || !end.isValid() || start.isSame(end, "day"));
  const dayName = oneDay ? start.format("dddd").toLowerCase() : "";
  const keepId = keepRoute ? routeId(keepRoute) : null;
  return (routes || []).filter((r) => {
    const day = getSingleWeekDay(r);
    if (!day) return true;
    if (keepId != null && routeId(r) == keepId) return true;
    return oneDay && day.toLowerCase() === dayName;
  });
}

function routeId(route) {
  return route && (route.id != null ? route.id : route.ID);
}

function excludeIds(routes, existing) {
  const ids = {};
  (existing || []).forEach((r) => {
    ids[routeId(r)] = true;
  });
  return (routes || []).filter((r) => !ids[routeId(r)]);
}

// Routes listed for another building (BusChangeRoutes), not already in the main list.
export function routesForBuilding(routes, building, mainRoutes) {
  const list = (routes || []).filter((r) =>
    splitCsv(r.buildings).some(
      (b) => b.toUpperCase() === String(building).trim().toUpperCase()
    )
  );
  return excludeIds(list, mainRoutes);
}

// Same building as the student(s), any grade, not already in the main list.
// Lets a child go home earlier on a younger grade's bus.
export function routesForOtherGrades(routes, students, mainRoutes) {
  const list = (students || []).filter(Boolean);
  const buildings = list
    .map((s) => String(s.building || "").trim())
    .filter(Boolean);
  if (!buildings.length) return [];
  const sameBuilding = (routes || []).filter(
    (r) =>
      splitCsv(r.buildings).length > 0 &&
      routeMatchesCsv(r.buildings, buildings)
  );
  return excludeIds(sameBuilding, mainRoutes);
}

// Every route listed for the student(s)' building, any grade (Friday / short-day picker).
export function routesForStudentBuildings(routes, students) {
  return routesForOtherGrades(routes, students, []);
}

// Picker sections keyed by the route's grade list (and direction), sorted by first grade.
export function groupRoutesByGrade(routes, showDirection = true) {
  const byKey = {};
  (routes || []).forEach((r) => {
    const grades = splitCsv(r.grades);
    const direction = String(r.direction || "").trim();
    const key = (grades.join(",").toUpperCase() || "ALL") + "|" + direction;
    if (!byKey[key]) {
      const gradeTitle = grades.length
        ? (grades.length === 1 ? "Grade " : "Grades ") + grades.join(", ")
        : "All grades";
      byKey[key] = {
        key: "grade-" + key,
        title:
          gradeTitle + (direction && showDirection ? " · " + direction : ""),
        sortKey:
          (grades.length ? grades[0].toUpperCase() : "~") + "|" + direction,
        routes: [],
      };
    }
    byKey[key].routes.push(r);
  });
  return Object.keys(byKey)
    .map((k) => byKey[k])
    .sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true })
    );
}

// Extra tile groups under the main list for BusRoutePicker:
// one per BusChangeRoutes building, plus same-building other-grade buses.
export function buildExtraRouteGroups(routes, students, mainRoutes, extraBuildings) {
  const list = (students || []).filter(Boolean);
  const ownBuildings = list
    .map((s) => String(s.building || "").trim())
    .filter(Boolean);
  const ownUpper = ownBuildings.map((b) => b.toUpperCase());
  const groups = [];

  if (ownBuildings.length) {
    const unique = ownBuildings.filter((b, i) => ownBuildings.indexOf(b) === i);
    groups.push({
      key: "other-grades",
      title: "Other grades in building " + unique.join(", "),
      label: "Building " + unique.join(", ") + " · other grades",
      shortLabel: "Building " + unique.join(", "),
      routes: routesForOtherGrades(routes, list, mainRoutes),
    });
  }

  (extraBuildings || []).forEach((building) => {
    if (ownUpper.includes(String(building).toUpperCase())) return;
    groups.push({
      key: "building-" + building,
      title: "Building " + building,
      label: "Building " + building,
      shortLabel: "Building " + building,
      routes: routesForBuilding(routes, building, mainRoutes),
    });
  });

  return groups.filter((g) => g.routes.length);
}

// One section per start time, earliest first — used by the Friday picker so the
// user picks a time instead of hunting through grade groups.
export function groupRoutesByTime(routes) {
  const byTime = {};
  (routes || []).forEach((r) => {
    const time = String(r.timeStart || "").trim();
    (byTime[time] = byTime[time] || []).push(r);
  });
  return Object.keys(byTime)
    .sort((a, b) => timeSortKey(a) - timeSortKey(b))
    .map((time) => ({
      key: "time-" + (time || "none"),
      title: time || "No start time",
      routes: byTime[time],
    }));
}

function timeSortKey(text) {
  const m = String(text || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!m) return 99999;
  let h = parseInt(m[1], 10);
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + parseInt(m[2], 10);
}

export function isRouteInPicker(route, mainRoutes, groups) {
  if (!route) return false;
  const has = (list) => (list || []).some((r) => r.id === route.id);
  return has(mainRoutes) || (groups || []).some((g) => has(g.routes));
}
